import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import SchoolIcon from "@mui/icons-material/School";
import { useAuth } from "@/auth/useAuth";
import { defaultRouteForRole } from "@/auth/nav";
import { extractErrorMessage } from "@/types/common";
import { registerTeacher, requestStudentLoginCode } from "@/api/auth";
import { fetchBranches, fetchSubjects } from "@/api/branches";
import { useTranslation } from "react-i18next";

const staffSchema = z.object({
  login: z.string().min(1, "Введите логин"),
  password: z.string().min(1, "Введите пароль"),
});
type StaffForm = z.infer<typeof staffSchema>;

const studentPhoneSchema = z.object({
  phone: z.string().min(9, "Введите номер телефона"),
});
type StudentPhoneForm = z.infer<typeof studentPhoneSchema>;

const studentCodeSchema = z.object({
  code: z.string().min(4, "Код из 4 цифр").max(6),
});
type StudentCodeForm = z.infer<typeof studentCodeSchema>;

const teacherRegisterSchema = z.object({
  fullName: z.string().min(2, "Введите ФИО"),
  phone: z.string().min(9, "Введите номер телефона"),
  branchId: z.string().min(1, "Выберите филиал"),
  subjectId: z.string().min(1, "Выберите предмет"),
  login: z.string().min(3, "Минимум 3 символа"),
  password: z.string().min(4, "Минимум 4 символа"),
});
type TeacherRegisterForm = z.infer<typeof teacherRegisterSchema>;

function AuthCard({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <Box className="crm-login">
      <Box className="crm-login__hero">
        <Box display="flex" alignItems="center" gap={1}>
          <SchoolIcon />
          <Typography fontFamily="var(--disp)" fontWeight={700}>Pro School</Typography>
        </Box>
        <Box>
          <Typography className="crm-login__title" variant="h1" fontWeight={700}>CRM учебного центра</Typography>
          <Typography className="crm-login__subtitle" mt={2}>Ученики, группы, расписание, финансы и уведомления — в одном окне, с телефона.</Typography>
          <Box className="crm-login__stats">
            <Box className="crm-login__stat"><b>CRM</b><span>единая система</span></Box>
            <Box className="crm-login__stat"><b>24/7</b><span>доступ к данным</span></Box>
          </Box>
        </Box>
        <Typography className="crm-login__subtitle" fontSize={12.5}>Учебный центр Pro School</Typography>
      </Box>
      <Box className="crm-login__form">
      <Paper className="crm-login__card" elevation={0} sx={{ p: 0, border: "none" }}>
        <Stack direction="row" alignItems="center" gap={1} justifyContent="center" mb={1}>
          <SchoolIcon color="primary" fontSize="large" />
          <Typography variant="h5" fontWeight={700}>
            {t("login.welcome")}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" mb={3}>
          {t("login.signIn")}
        </Typography>
        {children}
      </Paper>
      </Box>
    </Box>
  );
}

function StaffLoginTab() {
  const { loginWithPassword } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<StaffForm>({ resolver: zodResolver(staffSchema) });

  async function onSubmit(values: StaffForm) {
    setServerError(null);
    try {
      const user = await loginWithPassword(values);
      navigate(defaultRouteForRole(user.role), { replace: true });
    } catch (error) {
      setServerError(extractErrorMessage(error, "Неверный логин или пароль"));
    }
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing={2} mt={2}>
        {serverError && <Alert severity="error">{serverError}</Alert>}
        <TextField
          label={t("login.login")}
          fullWidth
          autoFocus
          {...register("login")}
          error={Boolean(errors.login)}
          helperText={errors.login?.message}
        />
        <TextField
          label={t("login.password")}
          type="password"
          fullWidth
          {...register("password")}
          error={Boolean(errors.password)}
          helperText={errors.password?.message}
        />
        <Button type="submit" variant="contained" size="large" disabled={isSubmitting}>
          {t("login.submit")}
        </Button>
      </Stack>
    </Box>
  );
}

function StudentLoginTab() {
  const { loginWithStudentCode } = useAuth();
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const [devHint, setDevHint] = useState<string | null>(null);
  const [step, setStep] = useState<"phone" | "code">("phone");
  const [phone, setPhone] = useState("");

  const phoneForm = useForm<StudentPhoneForm>({ resolver: zodResolver(studentPhoneSchema) });
  const codeForm = useForm<StudentCodeForm>({ resolver: zodResolver(studentCodeSchema) });

  async function onSubmitPhone(values: StudentPhoneForm) {
    setServerError(null);
    setDevHint(null);
    try {
      const response = await requestStudentLoginCode(values.phone);
      setPhone(values.phone);
      setStep("code");
      if (response.devCode) {
        setDevHint(`DEV-режим: код для входа — ${response.devCode}`);
      }
    } catch (error) {
      setServerError(extractErrorMessage(error, "Не удалось отправить код. Проверьте номер телефона."));
    }
  }

  async function onSubmitCode(values: StudentCodeForm) {
    setServerError(null);
    try {
      const user = await loginWithStudentCode(phone, values.code);
      navigate(defaultRouteForRole(user.role), { replace: true });
    } catch (error) {
      setServerError(extractErrorMessage(error, "Неверный код или он устарел."));
    }
  }

  if (step === "phone") {
    return (
      <Box component="form" onSubmit={phoneForm.handleSubmit(onSubmitPhone)} noValidate>
        <Stack spacing={2} mt={2}>
          {serverError && <Alert severity="error">{serverError}</Alert>}
          <TextField
            label="Номер телефона"
            placeholder="+998 90 123 45 67"
            fullWidth
            autoFocus
            {...phoneForm.register("phone")}
            error={Boolean(phoneForm.formState.errors.phone)}
            helperText={phoneForm.formState.errors.phone?.message}
          />
          <Button type="submit" variant="contained" size="large" disabled={phoneForm.formState.isSubmitting}>
            Получить код по SMS
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={codeForm.handleSubmit(onSubmitCode)} noValidate>
      <Stack spacing={2} mt={2}>
        {serverError && <Alert severity="error">{serverError}</Alert>}
        {devHint && <Alert severity="info">{devHint}</Alert>}
        <Typography variant="body2" color="text.secondary">
          Код отправлен на номер {phone}
        </Typography>
        <TextField
          label="Код из SMS"
          fullWidth
          autoFocus
          {...codeForm.register("code")}
          error={Boolean(codeForm.formState.errors.code)}
          helperText={codeForm.formState.errors.code?.message}
        />
        <Button type="submit" variant="contained" size="large" disabled={codeForm.formState.isSubmitting}>
          Войти
        </Button>
        <Button variant="text" onClick={() => setStep("phone")}>
          Изменить номер телефона
        </Button>
      </Stack>
    </Box>
  );
}

function TeacherRegisterTab() {
  const [serverError, setServerError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const { data: branches } = useQuery({ queryKey: ["branches"], queryFn: fetchBranches });
  const { data: subjects } = useQuery({ queryKey: ["subjects"], queryFn: fetchSubjects });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<TeacherRegisterForm>({ resolver: zodResolver(teacherRegisterSchema) });

  async function onSubmit(values: TeacherRegisterForm) {
    setServerError(null);
    try {
      await registerTeacher(values);
      setSuccess(true);
    } catch (error) {
      setServerError(extractErrorMessage(error, "Не удалось отправить заявку."));
    }
  }

  if (success) {
    return (
      <Alert severity="success" sx={{ mt: 2 }}>
        Заявка отправлена! Она появится в разделе «Преподаватели → Заявки на подтверждение».
        Как только администратор подтвердит заявку, вы сможете войти по указанному логину и паролю.
      </Alert>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing={2} mt={2}>
        {serverError && <Alert severity="error">{serverError}</Alert>}
        <TextField
          label="ФИО"
          fullWidth
          {...register("fullName")}
          error={Boolean(errors.fullName)}
          helperText={errors.fullName?.message}
        />
        <TextField
          label="Номер телефона"
          placeholder="+998 90 123 45 67"
          fullWidth
          {...register("phone")}
          error={Boolean(errors.phone)}
          helperText={errors.phone?.message}
        />
        <TextField
          select
          label="Филиал"
          fullWidth
          defaultValue=""
          {...register("branchId")}
          error={Boolean(errors.branchId)}
          helperText={errors.branchId?.message}
        >
          {(branches ?? []).map((branch) => (
            <MenuItem key={branch.id} value={branch.id}>
              {branch.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Предмет"
          fullWidth
          defaultValue=""
          {...register("subjectId")}
          error={Boolean(errors.subjectId)}
          helperText={errors.subjectId?.message}
        >
          {(subjects ?? []).map((subject) => (
            <MenuItem key={subject.id} value={subject.id}>
              {subject.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Логин"
          fullWidth
          {...register("login")}
          error={Boolean(errors.login)}
          helperText={errors.login?.message}
        />
        <TextField
          label="Пароль"
          type="password"
          fullWidth
          {...register("password")}
          error={Boolean(errors.password)}
          helperText={errors.password?.message}
        />
        <Button type="submit" variant="contained" size="large" disabled={isSubmitting}>
          Отправить заявку
        </Button>
        <Typography variant="caption" color="text.secondary" textAlign="center">
          Заявка попадёт на подтверждение администратору филиала. До подтверждения вход невозможен.
        </Typography>
      </Stack>
    </Box>
  );
}

export function LoginPage() {
  const [tab, setTab] = useState(0);
  const { t } = useTranslation();

  return (
    <AuthCard>
      <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="fullWidth">
        <Tab label={t("login.staff")} />
        <Tab label={t("login.student")} />
        <Tab label={t("login.teacherRegistration")} />
      </Tabs>
      {tab === 0 && <StaffLoginTab />}
      {tab === 1 && <StudentLoginTab />}
      {tab === 2 && <TeacherRegisterTab />}
    </AuthCard>
  );
}
